// Shared number/money formatting helpers.
//
// International decimal format = grouped thousands with a period decimal
// separator and (by default) two fraction digits, e.g. 1234567.5 -> "1,234,567.50".
// Use these everywhere monetary amounts are shown so the app stays consistent.

const INTL_LOCALE = "en-US";

/**
 * Format a monetary amount in the international decimal format (1,234,567.89).
 *
 * @param {number|string|null|undefined} value - the raw amount
 * @param {object} [options]
 * @param {number} [options.decimals=2] - number of fraction digits
 * @returns {string} the grouped, fixed-decimal string (returns "0.00" for invalid input)
 */
export const formatAmount = (value, { decimals = 2 } = {}) => {
  const num =
    typeof value === "string" ? Number(value.replace(/,/g, "")) : Number(value);

  const safe = Number.isFinite(num) ? num : 0;

  return safe.toLocaleString(INTL_LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format an integer-ish value (quantity, count) with grouped thousands and no
 * forced decimals, e.g. 1234 -> "1,234".
 */
export const formatNumber = (value) => formatAmount(value, { decimals: 0 });

/**
 * A monetary amount with its currency, e.g. 10677 -> "AED 10,677.00".
 * Use this wherever an amount is shown to the user (cells, cards, dialogs,
 * totals). Plain formatAmount is for numbers that are not displayed as money
 * (input values, column-width sizing, quantities via formatNumber).
 */
export const formatAED = (value, options) => `AED ${formatAmount(value, options)}`;

export default formatAmount;
